/**
 * AuthorListView — Grid of author cards. Default ("home") stage view.
 *
 * Reacts to `filter:change` events from FilterPanel and re-renders.
 * Emits `navigate:author` on card click.
 *
 * Every card carries a `data-fragment-id="author:<id>"` so the future
 * lesson-builder can treat the whole author dossier as a selectable unit too.
 */

import { BaseComponent } from '@/core/BaseComponent';
import { getDataService } from '@/services/data-registry';
import { escapeHtml, escapeAttr } from '@/core/escape';
import type { AuthorFilters } from '@/services/IDataService';
import type { AuthorCatalog } from '@/data/schema';
import { fragmentId } from '@/types/fragments';
import { periodLabel } from '@/data/vocab';
import type { UiLocale } from '@/data/vocab';
import { computeInitials } from '@/data/initials';

export class AuthorListView extends BaseComponent {
  private filters: AuthorFilters = {};
  private gridEl!: HTMLElement;
  private emptyEl!: HTMLElement;

  protected async onInit(): Promise<void> {
    // Single delegated listener for card clicks. Attached ONCE; survives
    // re-renders without piling up.
    this.addEventListener(this.element, 'click', (ev) => {
      const target = (ev.target as HTMLElement).closest<HTMLElement>('[data-author-id]');
      if (!target) return;
      const id = target.dataset.authorId!;
      this.context.bus.emit('navigate:author', { id });
    });

    // Filter subscription
    const off = this.context.bus.on<AuthorFilters>('filter:change', (filters) => {
      this.filters = filters;
      this.renderCards();
    });
    this.addCleanup(off);

    this.renderShell();
    this.renderCards();
  }

  protected onLocaleChange(): void {
    this.renderShell();
    this.renderCards();
  }

  private renderShell(): void {
    this.element.classList.add('author-list-view');
    this.element.innerHTML = `
      <header class="alv-header">
        <h1 class="alv-title">${this.t('app.name')}</h1>
        <p class="alv-tagline">${this.t('app.tagline')}</p>
      </header>
      <div class="alv-grid" role="list"></div>
      <div class="alv-empty" hidden>
        <p>${this.t('filter.empty')}</p>
      </div>
    `;
    this.gridEl = this.element.querySelector<HTMLElement>('.alv-grid')!;
    this.emptyEl = this.element.querySelector<HTMLElement>('.alv-empty')!;
  }

  private renderCards(): void {
    this.render();
  }

  private render(): void {
    const authors = getDataService().listAuthors(this.filters);
    if (authors.length === 0) {
      this.gridEl.innerHTML = '';
      this.emptyEl.hidden = false;
      return;
    }
    this.emptyEl.hidden = true;
    this.gridEl.innerHTML = authors.map(a => this.renderCard(a)).join('');
  }

  private renderCard(a: AuthorCatalog): string {
    const locale = this.context.locale as UiLocale;
    const dates = formatDates(a.birth_year, a.death_year);
    const periodChips = a.periods
      .map(p => `<span class="alv-chip">${escapeHtml(periodLabel(p, locale))}</span>`)
      .join('');
    const fid = fragmentId(a.id);

    return `
      <article class="alv-card${a.image ? '' : ' alv-card--fallback'}"
               role="listitem"
               data-author-id="${escapeAttr(a.id)}"
               data-fragment-id="${escapeAttr(fid)}"
               tabindex="0">
        ${a.image
          ? `<img class="alv-card-img" src="${escapeAttr(a.image)}" alt="" loading="lazy" />`
          : `<span class="alv-card-ini" aria-hidden="true">${escapeHtml(computeInitials(a.name))}</span>`}
        <div class="alv-card-scrim" aria-hidden="true"></div>
        <div class="alv-card-text">
          <h2 class="alv-card-name">${escapeHtml(a.name)}</h2>
          <div class="alv-card-meta">
            ${dates ? `<span class="alv-card-dates">${escapeHtml(dates)} · ${escapeHtml(a.nationality)}</span>` : ''}
            <div class="alv-card-chips">${periodChips}</div>
          </div>
        </div>
      </article>
    `;
  }

  private t(key: string): string {
    return this.context.i18n.t(key);
  }
}

function formatDates(birth: number | null | undefined, death: number | null | undefined): string {
  if (!birth && !death) return '';
  // Gli anni avanti Cristo sono NEGATIVI nei dati (serve all'ordinamento
  // cronologico). A schermo devono uscire «100 a.C.», non «-100»: su questo
  // tool riguarda una ventina di autori su trentasette.
  const era = (y: number): string => (y < 0 ? `${-y} a.C.` : `${y} d.C.`);
  if (birth == null) return `† ${era(death as number)}`;
  if (death == null) return `${era(birth)} – ?`;
  // Se stanno nella stessa era, l'indicazione si scrive una volta sola.
  const sameEra = birth < 0 === death < 0;
  return sameEra
    ? `${Math.abs(birth)}–${era(death)}`
    : `${era(birth)} – ${era(death)}`;
}

